import { ChangeEvent, DragEvent, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { Box, HStack, VStack } from "../../components/utils";
import { useBulkOnboardCompanies } from "../../queries/newCapTable/companyOnboarding";
import { useGetAllUserCompanies } from "../../queries/auth";
import { useAccountUsers } from "../../queries/companyUsers";
import { AssociatedCompany } from "../../types/UserDetailModel";
import {
  PrimaryCTAButton,
  ButtonSize,
} from "../quickRound/CTAButtonComponents";
import { CheckBoxWithLabel } from "../../components/shared/InputField";
import Tooltip from "../../components/shared/Tooltip";
import { formatSharePrice, formatWithCommas } from "./AddCompanyModal";

type Props = {
  open: boolean;
  onClose: () => void;
};

const TEMPLATE_URL =
  "https://equion-dev.s3.us-west-2.amazonaws.com/captable-templates/Company_Creation_Template.xlsx";

const ALLOWED_EXTENSIONS = ["xlsx", "xlsm"];

function isAllowedFile(file: File): boolean {
  const extension = file.name.split(".").pop()?.toLowerCase();
  return Boolean(extension && ALLOWED_EXTENSIONS.includes(extension));
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatExcelDate(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(value.getDate()).padStart(2, "0")}`;
  }
  if (typeof value === "number") {
    const date = XLSX.SSF.parse_date_code(value);
    if (date) {
      return `${date.y.toString().padStart(4, "0")}-${date.m
        .toString()
        .padStart(2, "0")}-${date.d.toString().padStart(2, "0")}`;
    }
  }
  const stringValue = String(value ?? "").trim();
  if (!stringValue) return "";
  const isoMatch = stringValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  // The bulk-company template formats dates as M/D/YYYY. Convert the text
  // ourselves instead of using Date(), which interprets it in a timezone.
  const monthFirstMatch = stringValue.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/
  );
  if (monthFirstMatch) {
    const [, month, day, year] = monthFirstMatch;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  // Do not parse date-only strings through UTC: it can move the displayed day
  // in timezones west of UTC. Retain unrecognised formats for the API instead.
  return stringValue;
}

function formatDateForDisplay(value?: string): string {
  if (!value) return "—";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
}

function findValuationDateColumn(sheet: XLSX.WorkSheet): number | undefined {
  const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
  // Headers are expected near the top, but scanning the first 20 rows makes
  // this resilient to a title row in a downloaded template.
  const lastHeaderRow = Math.min(range.e.r, range.s.r + 19);
  for (let row = range.s.r; row <= lastHeaderRow; row += 1) {
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
      if (String(cell?.v || "").trim().toLowerCase() === "valuation date") {
        return column;
      }
    }
  }
  return undefined;
}

async function normalizeBulkUploadFile(file: File): Promise<File> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const dateColumn = findValuationDateColumn(sheet);

  if (dateColumn === undefined) return file;

  const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
  for (let row = range.s.r; row <= range.e.r; row += 1) {
    const address = XLSX.utils.encode_cell({ r: row, c: dateColumn });
    const cell = sheet[address];
    if (cell?.v) {
      const date = formatExcelDate(cell.v);
      if (date) {
        // Store a literal date string. This prevents the bulk API from converting
        // an Excel serial/Date through UTC and changing the day.
        sheet[address] = { t: "s", v: date, w: date };
      }
    }
  }

  const extension = file.name.split(".").pop()?.toLowerCase();
  const bookType = extension === "xlsm" ? "xlsm" : "xlsx";
  const normalizedWorkbook = XLSX.write(workbook, {
    type: "array",
    bookType,
    bookVBA: extension === "xlsm",
  });
  return new File([normalizedWorkbook], file.name, { type: file.type });
}

type ParsedCompanyRow = {
  name: string;
  cin: string;
  jurisdiction: string;
  valuation?: string;
  valuationDate?: string;
  sharePrice?: string;
  emails?: string;
};

// The template's header row, lower-cased/trimmed, mapped to our field names —
// matches the columns in Company_Creation_Template.xlsx (Company Name, Cin,
// Jurisdiction, Valuation, Valuation Date, Share Price, Emails).
const HEADER_TO_FIELD: Record<string, keyof ParsedCompanyRow> = {
  "company name": "name",
  cin: "cin",
  jurisdiction: "jurisdiction",
  valuation: "valuation",
  "valuation date": "valuationDate",
  "share price": "sharePrice",
  emails: "emails",
};

async function parseCompanyRows(file: File): Promise<ParsedCompanyRow[]> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rawRows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, {
    defval: "",
  });

  return rawRows
    .map((rawRow) => {
      const row: Partial<ParsedCompanyRow> = {};
      Object.entries(rawRow).forEach(([header, value]) => {
        const field = HEADER_TO_FIELD[header.trim().toLowerCase()];
        if (field && value !== "") {
          // Excel returns numeric cells (valuation, share price, cin) as
          // numbers, not strings — normalize everything to string so the
          // diffing/formatting below (.trim(), .split()) doesn't crash.
          (row as Record<string, unknown>)[field] =
            field === "valuationDate"
              ? formatExcelDate(value)
              : typeof value === "string"
                ? value.trim()
                : String(value);
        }
      });
      return row as ParsedCompanyRow;
    })
    .filter((row) => row.name || row.cin);
}

// The template has no company-id column — new companies get one on the
// backend — so "same company" for a bulk row means "an existing company with
// this CIN", which is what actually determines create-vs-update server-side.
function findMatchingCompany(
  row: ParsedCompanyRow,
  companies: AssociatedCompany[]
): AssociatedCompany | undefined {
  const cin = row.cin?.trim().toLowerCase();
  if (!cin) return undefined;
  return companies.find((c) => c.cin?.trim().toLowerCase() === cin);
}

function isStageFourCompany(company: AssociatedCompany): boolean {
  const stage = String(company.stage || "")
    .trim()
    .toLowerCase();
  return stage === "4" || stage === "four" || stage === "stage 4";
}

function isStageThreeCompany(company: AssociatedCompany): boolean {
  const stage = String(company.stage || "")
    .trim()
    .toLowerCase();
  return stage === "3" || stage === "three" || stage === "stage 3";
}

function isStageThreeOrFourCompany(company: AssociatedCompany): boolean {
  return isStageThreeCompany(company) || isStageFourCompany(company);
}

type DiffField = {
  label: string;
  oldValue?: string;
  newValue?: string;
};

// Only fields present on both the sheet and the existing company record can
// be diffed; valuationDate/emails aren't returned on AssociatedCompany.
function getDiffFields(
  row: ParsedCompanyRow,
  match: AssociatedCompany
): DiffField[] {
  const fields: DiffField[] = [
    { label: "Name", oldValue: match.nameOfTheCompany, newValue: row.name },
    {
      label: "Jurisdiction",
      oldValue: match.jurisdiction,
      newValue: row.jurisdiction,
    },
    {
      label: "Valuation",
      oldValue:
        match.valuation !== undefined ? String(match.valuation) : undefined,
      newValue: row.valuation,
    },
    {
      label: "Share Price",
      oldValue:
        match.sharePrice !== undefined ? String(match.sharePrice) : undefined,
      newValue: row.sharePrice,
    },
  ];
  return fields.filter(
    (f) =>
      f.oldValue !== undefined &&
      f.newValue !== undefined &&
      f.oldValue.trim() !== f.newValue.trim()
  );
}

export default function BulkUploadCompanyModal({ open, onClose }: Props) {
  const excelInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [previewRows, setPreviewRows] = useState<ParsedCompanyRow[] | null>(
    null
  );
  const [giveAccessToAllUsers, setGiveAccessToAllUsers] = useState(false);
  const { mutate: bulkOnboardCompanies, isLoading: isUploading } =
    useBulkOnboardCompanies();
  const { data: existingCompanies } = useGetAllUserCompanies();
  const { data: accountUsers } = useAccountUsers();
  const companies = existingCompanies || [];
  const allAccountUserEmails = Array.from(
    new Set(
      (accountUsers || [])
        .map((u: any) => (u?.emailId || u?.email || "").trim())
        .filter(Boolean)
    )
  );

  const resetState = () => {
    setFile(null);
    setIsDragging(false);
    setPreviewRows(null);
    setGiveAccessToAllUsers(false);
  };

  const handleClose = () => {
    if (isUploading || isParsing) return;
    resetState();
    onClose();
  };

  const previewFile = async (fileToParse: File) => {
    setIsParsing(true);
    setPreviewRows(null);
    try {
      const normalizedFile = await normalizeBulkUploadFile(fileToParse);
      const rows = await parseCompanyRows(normalizedFile);
      if (rows.length === 0) {
        toast.error(
          "No company rows were found in this file. Check that it matches the template."
        );
        return;
      }
      setFile(normalizedFile);
      setPreviewRows(rows);
    } catch (error: any) {
      toast.error(
        error?.message || "Failed to read the excel file. Check the format."
      );
    } finally {
      setIsParsing(false);
    }
  };

  const acceptFile = (incoming: File | undefined | null) => {
    if (!incoming) return;
    if (!isAllowedFile(incoming)) {
      toast.error(
        `Please upload a valid Excel file (.${ALLOWED_EXTENSIONS.join(", .")})`
      );
      return;
    }
    setFile(incoming);
    previewFile(incoming);
  };

  const handleExcelUploadClick = () => {
    excelInputRef.current?.click();
  };

  const handleExcelSelect = (e: ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const handleUpload = () => {
    if (!file) return;
    bulkOnboardCompanies(
      {
        file,
        emails: giveAccessToAllUsers ? allAccountUserEmails : undefined,
      },
      {
        onSuccess: (result: any) => {
          const total = result?.total;
          const succeeded = result?.succeeded;
          const failed = result?.failed;
          const message =
            typeof total === "number" && typeof succeeded === "number"
              ? `Processed ${succeeded} of ${total} compan${
                  total === 1 ? "y" : "ies"
                }${failed ? `; ${failed} failed` : ""}.`
              : "Companies processed successfully.";
          if (failed) {
            toast.error(message);
            return;
          }
          toast.success(message);
          resetState();
          onClose();
        },
        onError: (err: any) => {
          const msg =
            err?.response?.data?.errorMessage ||
            err?.response?.data?.message ||
            err?.response?.data?.reason ||
            "Failed to upload and onboard companies.";
          toast.error(msg);
        },
      }
    );
  };

  const rowsWithMatches = (previewRows || []).map((row) => {
    const match = findMatchingCompany(row, companies);
    return { row, match, diffFields: match ? getDiffFields(row, match) : [] };
  });
  const newCount = rowsWithMatches.filter((r) => !r.match).length;
  const existingCount = rowsWithMatches.length - newCount;
  const stageThreeRows = rowsWithMatches.filter(
    ({ match }) => match && isStageThreeCompany(match)
  );
  const stageFourRows = rowsWithMatches.filter(
    ({ match }) => match && isStageFourCompany(match)
  );
  const changedCount = rowsWithMatches.filter(
    (r) =>
      r.match && !isStageThreeOrFourCompany(r.match) && r.diffFields.length > 0
  ).length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={previewRows ? "lg" : "md"}
      fullWidth
      PaperProps={{ className: previewRows ? "!max-w-4xl" : "!max-w-2xl" }}
    >
      <Box className="p-6">
        <HStack className="items-center justify-between pb-4">
          <p className="text-base font-semibold text-stone-800">
            Bulk Upload Company
          </p>
          <XMarkIcon
            className="h-6 w-6 shrink-0 cursor-pointer text-stone-500 hover:text-stone-700"
            onClick={handleClose}
          />
        </HStack>

        <VStack className="gap-3">
          <VStack className="rounded-md border border-stone-200 overflow-hidden">
            <div
              onClick={handleExcelUploadClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center gap-2 py-10 px-6 text-center cursor-pointer transition-colors ${
                isDragging ? "bg-primary/5" : "hover:bg-stone-100 bg-stone-50"
              }`}
            >
              <input
                ref={excelInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                hidden
                onChange={handleExcelSelect}
              />
              <Icon
                icon="material-symbols:cloud-upload"
                className="text-primary shrink-0"
                height={32}
              />
              <p className="text-sm font-medium text-stone-700">
                Drag & drop an excel file here, or{" "}
                <span className="text-primary underline">browse</span>
              </p>
              <p className="text-xs text-stone-400">
                (Supports{" "}
                {ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(", ")} files)
              </p>
            </div>

            {file && (
              <HStack className="items-center justify-between gap-3 border-t border-stone-200 px-4 py-3">
                <HStack className="items-center gap-3 min-w-0">
                  <Icon
                    icon="mdi:file-excel-outline"
                    className="text-green-700 shrink-0"
                    height={22}
                  />
                  <VStack className="min-w-0">
                    <p className="text-sm text-stone-700 truncate max-w-sm">
                      {file.name}
                    </p>
                    <p className="text-xs text-stone-400">
                      {formatFileSize(file.size)}
                    </p>
                  </VStack>
                </HStack>
                <Icon
                  icon="mdi:close"
                  height={18}
                  className="shrink-0 cursor-pointer text-stone-400 hover:text-stone-600"
                  onClick={() => {
                    setFile(null);
                    setPreviewRows(null);
                  }}
                />
              </HStack>
            )}
          </VStack>

          <p className="text-xs text-gray-400">
            Download the template{" "}
            <a
              href={TEMPLATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 underline hover:text-blue-800"
            >
              here
            </a>
          </p>

          {isParsing && (
            <HStack className="items-center justify-center gap-2 py-6">
              <Icon
                icon="lucide:loader-2"
                className="animate-spin text-primary"
                height={20}
              />
              <p className="text-sm text-stone-500">
                Reading file and extracting company details...
              </p>
            </HStack>
          )}

          {!isParsing && previewRows && (
            <VStack className="gap-3">
              <HStack className="items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
                <Icon
                  icon="mdi:information-outline"
                  className="text-blue-500 shrink-0 mt-0.5"
                  height={18}
                />
                <VStack className="gap-1 text-xs text-blue-700">
                  <p>
                    {rowsWithMatches.length} compan
                    {rowsWithMatches.length === 1 ? "y" : "ies"} found — {newCount} new and{" "}
                    {existingCount} existing.
                  </p>

                  {changedCount > 0 && (
                    <p>
                      {changedCount} existing compan
                      {changedCount === 1 ? "y has" : "ies have"} updated details that will be
                      saved.
                    </p>
                  )}

                  {stageFourRows.length > 0 && (
                    <VStack className="gap-1">
                      <p className="font-semibold">
                        Existing Stage 4 compan
                        {stageFourRows.length === 1 ? "y" : "ies"}:
                      </p>
                      <ul className="list-disc space-y-1 pl-4">
                        {stageFourRows.map(({ row, match }, index) => (
                          <li key={`${match?.uuid || row.cin}-${index}`}>
                            <span className="font-medium">
                              {match?.nameOfTheCompany || row.name}
                            </span>{" "}
                            (CIN: {row.cin || match?.cin || "—"}) exists in
                            Stage 4. No company details will be created or
                            updated; only missing user access will be granted.
                          </li>
                        ))}
                      </ul>
                    </VStack>
                  )}

                  {stageThreeRows.length > 0 && (
                    <VStack className="gap-1">
                      <p className="font-semibold">
                        Existing Stage 3 compan
                        {stageThreeRows.length === 1 ? "y" : "ies"}:
                      </p>
                      <ul className="list-disc space-y-1 pl-4">
                        {stageThreeRows.map(({ row, match }, index) => (
                          <li key={`${match?.uuid || row.cin}-${index}`}>
                            <span className="font-medium">
                              {match?.nameOfTheCompany || row.name}
                            </span>{" "}
                            (CIN: {row.cin || match?.cin || "—"}) exists in
                            Stage 3. No company details will be created or
                            updated; only missing user access will be granted.
                          </li>
                        ))}
                      </ul>
                    </VStack>
                  )}
                </VStack>
              </HStack>

              <Box className="border border-stone-200 rounded-md overflow-auto max-h-[50vh]">
                <table className="min-w-full text-sm">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr>
                      {[
                        "Company Name",
                        "CIN",
                        "Jurisdiction",
                        "Valuation",
                        "Valuation Date",
                        "Share Price",
                      ].map((label) => (
                        <th
                          key={label}
                          className="px-3 py-2 text-left text-xs font-semibold text-stone-500 whitespace-nowrap"
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {rowsWithMatches.map(({ row }, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <tr key={index}>
                        <td className="px-3 py-2 align-top text-stone-700">
                          {row.name || "—"}
                        </td>
                        <td className="px-3 py-2 align-top text-stone-700">
                          {row.cin || "—"}
                        </td>
                        <td className="px-3 py-2 align-top text-stone-700">
                          {row.jurisdiction || "—"}
                        </td>
                        <td className="px-3 py-2 align-top text-stone-700">
                          {row.valuation
                            ? formatWithCommas(row.valuation)
                            : "—"}
                        </td>
                        <td className="px-3 py-2 align-top text-stone-700">
                          {formatDateForDisplay(row.valuationDate)}
                        </td>
                        <td className="px-3 py-2 align-top text-stone-700">
                          {row.sharePrice
                            ? formatSharePrice(row.sharePrice)
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </VStack>
          )}

          {file && (
            <HStack className="items-center gap-2">
              <CheckBoxWithLabel
                id="bulk-add-all-users"
                label="Add all account users to these companies"
                labelclassname="text-sm text-stone-600 ml-2"
                checked={giveAccessToAllUsers}
                onChange={(e) => setGiveAccessToAllUsers(e.target.checked)}
              />
              <Tooltip
                _className="w-36"
                text="Grants every user on your account access to each company in this file"
              >
                <Icon
                  icon="material-symbols:info-outline"
                  width={20}
                  className="text-gray-400"
                  height={20}
                />
              </Tooltip>
            </HStack>
          )}

          <HStack className="justify-end pt-2">
            <PrimaryCTAButton
              className="min-w-[160px] flex justify-center text-center"
              event-name="Upload Excel for Bulk Onboard Companies"
              buttonSize={ButtonSize.MEDIUM}
              onClick={handleUpload}
              disabled={!file || isParsing || isUploading}
            >
              {isUploading ? "Uploading..." : "Upload Companies"}
            </PrimaryCTAButton>
          </HStack>
        </VStack>
      </Box>
    </Dialog>
  );
}
